import { ReactNode } from 'react';
import { CalendarIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { ExclamationCircleIcon } from '@heroicons/react/16/solid';

import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { twJoin } from 'tailwind-merge';

import ScheduledLessonWeekCalendar from '@/components/scheduled-lesson/ScheduledLessonWeekCalendar';
import { Button } from '@/components/ui/daisy/button';
import useAuth from '@/data/hook/useAuth';
import { KlassStatusEnum } from '@/models/Klass';
import { ROUTES } from '@/utils/routes';

const ACTIVE_KLASSES_TITLE_ID = 'active-klasses-title';

type LegendItemProps = {
  icon: ReactNode;
  colorClassName: string;
  label: string;
};

function LegendItem({ icon, colorClassName, label }: LegendItemProps) {
  return (
    <li
      className={twJoin(
        'flex items-center gap-2 text-xs font-500',
        colorClassName,
      )}
    >
      {icon}
      {label}
    </li>
  );
}

export default function ActiveKlassesSection() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('translation', {
    keyPrefix: 'teacherDashboard.activeKlasses',
  });

  return (
    <section
      aria-labelledby={ACTIVE_KLASSES_TITLE_ID}
      className="flex flex-col gap-6"
    >
      <header className="flex items-center justify-between">
        <h2
          id={ACTIVE_KLASSES_TITLE_ID}
          className="text-xl font-medium text-base-content leading-8"
        >
          {t('title')}
        </h2>
        <Button
          size="sm"
          variant="outline"
          color="neutral"
          onClick={() => navigate(ROUTES.ADMIN.CLASSES.BASE)}
        >
          <ArrowTopRightOnSquareIcon className="size-3" />
          {t('seeAll')}
        </Button>
      </header>
      <ul className="flex items-center gap-4 self-end">
        <LegendItem
          icon={<CalendarIcon className="size-2.5" />}
          colorClassName="text-success"
          label={t('legend.attendances')}
        />
        <LegendItem
          icon={<DocumentTextIcon className="size-2.5" />}
          colorClassName="text-warning"
          label={t('legend.exams')}
        />
        <LegendItem
          icon={<ExclamationCircleIcon className="size-3" />}
          colorClassName="text-error"
          label={t('legend.requiresAttention')}
        />
      </ul>
      <ScheduledLessonWeekCalendar
        filters={{
          teacher: user?.id,
          klassStatus: [KlassStatusEnum.IN_PROGRESS, KlassStatusEnum.OPEN],
        }}
      />
    </section>
  );
}
